import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Undo2, Search, FileSpreadsheet, ArrowRightLeft, Eye, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, KpiCard } from "@/components/KpiCard";
import { RentalDialog } from "@/components/rentals/RentalDialog";
import { ReturnDialog } from "@/components/rentals/ReturnDialog";
import { RentalView } from "@/components/rentals/RentalView";
import { DriveConnect } from "@/components/damages/DriveConnect";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, stationColor } from "@/lib/format";
import { useSort, SortTh } from "@/lib/sort";

const Pill = ({ s }) => (
  <span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s === "ATTIVO" ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-200 bg-slate-100 text-slate-600"}`}>{s === "ATTIVO" ? "In noleggio" : "Riconsegnato"}</span>
);
const km = (n) => (n == null ? "—" : n.toLocaleString("it-IT"));

export default function RentalsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ rentals: [], missing: [], summary: {} });
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [returning, setReturning] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("ATTIVO");
  const [viewing, setViewing] = useState(null);

  const sid = isAdmin && stationId !== "all" ? stationId : undefined;
  const load = useCallback(() => {
    api.get("/rentals", { params: { station_id: sid } }).then((r) => setData(r.data));
    api.get("/fleet/vehicles", { params: { station_id: sid } }).then((r) => setVehicles([...r.data.vehicles, ...(r.data.incoming || []).map((v) => ({ ...v, incoming: true }))]));
  }, [sid]);
  useEffect(load, [load]);
  useEffect(() => { api.get("/stations").then((r) => setStations(r.data)); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la scheda noleggio di ${r.plate}?`)) return;
    try { await api.delete(`/rentals/${r.id}`); toast.success("Noleggio eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const done = () => { setEditing(null); setReturning(null); setViewing(null); load(); };
  const exportXlsx = async () => {
    try {
      const { data: blob, headers } = await api.get("/rentals/export", { params: { station_id: sid, q: q || undefined }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "noleggi.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const { missing, summary } = data;
  const rentals = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = data.rentals.filter((r) => r.status === tab);
    if (!t) return list;
    return list.filter((r) => [r.plate, r.model, r.provider, r.location, r.station_name, r.transfer_station_name, r.damage_notes, r.return_notes].some((x) => (x || "").toLowerCase().includes(t)));
  }, [data.rentals, q, tab]);
  const { sorted, sort, toggle } = useSort(rentals, { photos: (r) => r.photos.pickup.length + r.photos.return.length, damage: (r) => r.damage_notes || r.return_notes });
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;
  const busy = new Set(data.rentals.filter((r) => r.status === "ATTIVO").map((r) => r.vehicle_id));
  const title = isAdmin ? (stationId === "all" ? "Noleggi" : `Noleggi ${stations.find((s) => s.id === stationId)?.name || ""}`) : `Noleggi ${stations[0]?.name || ""}`;

  return (
    <div className="fade-up">
      <PageHeader title={title} subtitle="Mezzi a breve termine: ritiro, km, danni e foto, riconsegna.">
        <Button variant="outline" onClick={exportXlsx} data-testid="rental-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({ station_id: sid })} data-testid="rental-add-button"><Plus className="mr-2 h-4 w-4" /> Nuovo noleggio</Button>
      </PageHeader>

      {isAdmin && <DriveConnect />}
      {isAdmin && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="rental-station-tabs">
          {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((s) => (
            <button key={s.id} onClick={() => setStationId(s.id)} data-testid={`rental-tab-${s.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === s.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {s.id !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, s.id) }} />}{s.name}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label={sid || !isAdmin ? "Noleggiati della stazione" : "Noleggiati totali"} value={summary.fleet_total ?? 0} accent="#0284C7" testId="rental-kpi-total" />
        <KpiCard label="Trasferiti ad altre sedi" value={`− ${summary.fleet_out ?? 0}`} accent="#DC2626" testId="rental-kpi-out" />
        <KpiCard label="Arrivati da altre sedi" value={`+ ${summary.fleet_in ?? 0}`} accent="#059669" testId="rental-kpi-in" />
        <KpiCard label="Effettivi in stazione" value={summary.fleet_effective ?? 0} accent="#0F172A" testId="rental-kpi-effective" />
      </div>
      <div className="mb-6 grid grid-cols-3 gap-4">
        <KpiCard label="Schede attive" value={summary.active ?? 0} accent="#7C3AED" testId="rental-kpi-active" />
        <KpiCard label="Riconsegnati" value={summary.returned ?? 0} accent="#475569" testId="rental-kpi-returned" />
        <KpiCard label="Senza scheda" value={summary.missing ?? 0} accent="#D97706" testId="rental-kpi-missing" />
      </div>

      {missing.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="rental-missing">
          <p className="mb-2 text-sm font-semibold text-amber-800">Mezzi a breve termine in flotta senza scheda noleggio</p>
          <div className="flex flex-wrap gap-2">
            {missing.map((v) => (
              <button key={v.id} onClick={() => setEditing({ vehicle_id: v.id, provider: v.provider, station_id: v.station_id })} data-testid={`rental-missing-${v.id}`}
                className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm hover:border-amber-500">
                <span className="font-mono-num font-semibold">{v.plate}</span> <span className="text-slate-500">{v.model}{(isAdmin || v.incoming) && ` · ${v.station_name}`}{v.incoming && " (trasferito qui)"}</span> <span className="ml-1 text-amber-700">Compila</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="rental-view-tabs">
          {[["ATTIVO", `Mezzi in noleggio (${summary.active ?? 0})`], ["RICONSEGNATO", `Storico riconsegnati (${summary.returned ?? 0})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`rental-tab-status-${k}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca targa, modello, noleggio, luogo, danni..." className="pl-9" data-testid="rental-search-input" />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="rental-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
              <tr><S k="plate">Targa</S>{isAdmin && <S k="station_name">Stazione</S>}<S k="provider">Noleggio</S><S k="location">Luogo</S><S k="pickup_date">Ritiro</S>
                <S k="km_start" className="px-3 py-2 text-right" align="right">Km iniziali</S><S k="return_date">Riconsegna</S><S k="km_end" className="px-3 py-2 text-right" align="right">Km finali</S><S k="km_driven" className="px-3 py-2 text-right" align="right">Km percorsi</S>
                <S k="damage">Danni</S><S k="photos">Foto</S><S k="status">Stato</S><th className="px-3 py-2" /></tr>
            </thead>
            <tbody className="divide-y">
              {sorted.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setViewing(r)} data-testid={`rental-row-${r.id}`}>
                  <td className="whitespace-nowrap px-3 py-2"><span className="font-mono-num rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-semibold">{r.plate}</span><span className="ml-2 text-xs text-slate-500">{r.model}</span>
                    {r.transfer_station_id && <span className="mt-1 flex items-center gap-1 text-xs font-medium text-violet-700" data-testid={`rental-transfer-${r.id}`}><ArrowRightLeft className="h-3 w-3" />{sid && r.station_id !== sid ? `Da ${r.station_name}` : `Trasferito a ${r.transfer_station_name}`}</span>}</td>
                  {isAdmin && <td className="whitespace-nowrap px-3 py-2">{r.station_name}</td>}
                  <td className="whitespace-nowrap px-3 py-2">{r.provider}</td>
                  <td className="px-3 py-2 text-slate-600">{r.location || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2">{fmtDate(r.pickup_date)}</td>
                  <td className="font-mono-num px-3 py-2 text-right">{km(r.km_start)}</td>
                  <td className="whitespace-nowrap px-3 py-2">{r.return_date ? fmtDate(r.return_date) : "—"}</td>
                  <td className="font-mono-num px-3 py-2 text-right">{km(r.km_end)}</td>
                  <td className="font-mono-num px-3 py-2 text-right font-semibold" data-testid={`rental-km-${r.id}`}>{km(r.km_driven)}</td>
                  <td className="max-w-xs px-3 py-2 text-xs text-slate-600"><p className="line-clamp-2">{[r.damage_notes && `Ritiro: ${r.damage_notes}`, r.return_notes && `Riconsegna: ${r.return_notes}`].filter(Boolean).join(" · ") || "—"}</p></td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600"><ImageIcon className="mr-1 inline h-4 w-4" />{r.photos.pickup.length}/{r.photos.return.length}</td>
                  <td className="px-3 py-2"><Pill s={r.status} /></td>
                  <td className="whitespace-nowrap px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => setViewing(r)} data-testid={`rental-view-${r.id}`}><Eye className="h-4 w-4" /></Button>
                    <Button size="sm" variant={r.status === "ATTIVO" ? "outline" : "ghost"} onClick={() => setReturning(r)} data-testid={`rental-return-${r.id}`}><Undo2 className="h-4 w-4" />{r.status === "ATTIVO" && <span className="ml-1">Riconsegna</span>}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`rental-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(r)} data-testid={`rental-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
              {rentals.length === 0 && <tr><td colSpan={13} className="px-3 py-10 text-center text-slate-500" data-testid="rental-empty">{q ? "Nessun noleggio corrisponde alla ricerca." : tab === "ATTIVO" ? "Nessun mezzo in noleggio." : "Nessun noleggio riconsegnato."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <RentalDialog rental={editing} vehicles={vehicles.filter((v) => !busy.has(v.id) && v.contract_type === "BT")} stations={stations} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={done} />
      <ReturnDialog rental={returning} onClose={() => setReturning(null)} onSaved={done} />
      <RentalView rental={viewing} onClose={() => setViewing(null)} onEdit={(r) => { setViewing(null); setEditing(r); }} onReturn={(r) => { setViewing(null); setReturning(r); }} />
    </div>
  );
}
